import {
  BusinessGetResponseType,
  BusinessUpdateResponseType,
} from "@sellar-io/api-v2/types";
import { Button } from "@sellar-io/components";
import { useForm } from "@tanstack/react-form";
import { useId } from "react";
import { useNavigate } from "react-router";
import { SpinnerLegacy } from "~/components";
import { Alert } from "~/components/alert/Alert";
import { BUSINESS_SETTINGS } from "~/constants/routes";
import { useEvents } from "~/hooks";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { useBusinessUpdate } from "~/hooks/api/v2/business/useBusinessUpdate";
import { BusinessForm } from "./BusinessForm";
import {
  generateBusinessFormFieldValues,
  generateBusinessRequest,
  type BusinessFormFieldValues,
} from "./BusinessFormValues";

export const BusinessFormUpdate = ({
  business,
  onUpdate,
}: {
  business: BusinessGetResponseType;
  onUpdate: (business: BusinessUpdateResponseType) => void;
}) => {
  const { track } = useEvents();
  const formId = useId();
  const navigate = useNavigate();
  const businessUpdateMutation = useBusinessUpdate(business.id);

  const form = useForm({
    defaultValues: {
      ...generateBusinessFormFieldValues(business),
      hasNoAwrsNumber: !business.awrsNumber,
      hasNoVatNumber: !business.vatNumber,
      email: business.email,
    } satisfies BusinessFormFieldValues,
    onSubmit: ({ value }) => {
      const request = generateBusinessRequest(value, business.type);
      businessUpdateMutation.mutate(
        {
          ...request,
          awrsNumber: value.hasNoAwrsNumber ? null : value.awrsNumber,
          vatNumber: value.hasNoVatNumber ? null : value.vatNumber,
        },
        {
          onSuccess: (businessResponse) => {
            track(
              business.type === "supplier"
                ? "Supplier Updated"
                : "Retailer Updated",
            );
            navigate(BUSINESS_SETTINGS);
            onUpdate(businessResponse);
          },
        },
      );
    },
  });

  if (businessUpdateMutation.isPending) return <SpinnerLegacy />;

  return (
    <>
      {businessUpdateMutation.isError ? (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(businessUpdateMutation.error)?.response?.data
              .message ?? "Unknown error"
          }
        />
      ) : null}
      <BusinessForm
        type={business.type === "retailer" ? "buyer" : "supplier"}
        form={form}
        formId={formId}
      />
      <div className="mt-4">
        <Button type="submit" form={formId}>
          Save
        </Button>
      </div>
    </>
  );
};
